/*
    Accepts:
    – nodeIdToInjectActiveIndex: String – Node id in which active tab index will be injected (optional)
    – closeOtherTabsOnOpen: String (true/false, default is false) – Whether other tabs, besides the active one should close on new tab click
    - closeOnOutsideClick: String (boolean-like true/false) – Close tabs on click outside
 */

class ProductTabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('.product__tabs-tab-wrapper');
    if (this.dataset.nodeIdToInjectActiveIndex) {
      this.nodeToInjectActiveIndex = document.querySelector(`#${this.dataset.nodeIdToInjectActiveIndex}`)
    }
    this.closeOtherTabsOnOpen = this.dataset.closeOtherTabsOnOpen === 'true'
    this.closeOnOutsideClick = this.dataset.closeOnOutsideClick === 'true'

    this.tabs.forEach((tab, tabIndex) => {
      const tabExpandableContent = tab.querySelector('.product__tabs-tab-inner');
      const tabLabel = tab.querySelector('.product__tabs-tab-row');
      if (!tabExpandableContent) {
        return (tab.style.display = 'none');
      }
      tabExpandableContent.ariaHidden = true;

      tabLabel.addEventListener('click', () => {
        // if closeOtherTabsOnOpen option enabled, close other tabs besides active one
        if (this.closeOtherTabsOnOpen) {
          this.tabs.forEach((tabToClose) => {
            if (tabToClose !== tab) {
              this.removeTabActiveState(tabToClose);

              if (this.nodeToInjectActiveIndex) {
                this.nodeToInjectActiveIndex.dataset.activeIndex = ""
              }
            }
          })
        }

        if (tab.classList.contains('active')) {
          this.removeTabActiveState(tab);

          if (this.nodeToInjectActiveIndex) {
            this.nodeToInjectActiveIndex.dataset.activeIndex = ""
          }
        } else {
          this.showTabDescription(tab);
          if (this.nodeToInjectActiveIndex) {
            this.nodeToInjectActiveIndex.dataset.activeIndex = String(tabIndex)
          }
        }

        if (this.closeOnOutsideClick) {
          document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) {
              this.tabs.forEach((tabToClose) => this.removeTabActiveState(tabToClose))

              if (this.nodeToInjectActiveIndex) {
                this.nodeToInjectActiveIndex.dataset.activeIndex = ""
              }
            }
          })
        }
      });
    });
  }

  getTallestChildHeight(wrapper) {
    if (!wrapper || wrapper.children.length === 0) {
      return 0; // Return 0 if no wrapper or no children
    }

    // Initialize the tallest height found to 0
    let tallestHeight = 0;

    for (const child of wrapper.children) {
      const childHeight = child.offsetHeight;

      if (childHeight > tallestHeight) {
        tallestHeight = childHeight;
      }
    }

    return tallestHeight;
  }

  removeTabActiveState = (tab) => {
    tab.classList.remove('active');
    tab.dataset.active = 'false';
    const tabExpandableContent = tab.querySelector('.product__tabs-tab-inner');

    if (tabExpandableContent) {
      tabExpandableContent.ariaHidden = true;
      tabExpandableContent.classList.remove('product__tabs-tab-inner--expanded');
      tabExpandableContent.classList.add('product__tabs-tab-inner--collapsed');
      tabExpandableContent.style = `max-height: 0px;`;
    }
  };

  assignStylesToChild = (child) => {
    if (child.tagName === 'P') {
      child.style = 'margin: 0;padding-left: 24px;';
    }

    child.style = 'word-break: break-word;';
  };

  assignStylesToChildren = (children) =>
    Array.from(children).forEach((child) => {
      if (child.children.length) {
        this.assignStylesToChildren(child.children);
      } else {
        this.assignStylesToChild(child);
      }
    });

  showTabDescription = (tab) => {
    tab.classList.add('active');
    tab.dataset.active = 'true';

    const tabExpandableContent = tab.querySelector('.product__tabs-tab-inner');
    if (!tabExpandableContent) return;

    tabExpandableContent.ariaHidden = false;
    this.assignStylesToChildren(tabExpandableContent.children);

    const CONTENT_PADDING_PX = 80;
    const tabDescriptionHeight = this.getTallestChildHeight(tabExpandableContent) + CONTENT_PADDING_PX;

    tabExpandableContent.classList.add('product__tabs-tab-inner--expanded');
    tabExpandableContent.classList.remove('product__tabs-tab-inner--collapsed');

    tabExpandableContent.style = `max-height: ${tabDescriptionHeight}px;`;
  };

  disconnectedCallback() {}
}

customElements.define('product-tabs', ProductTabs);
